import { FieldType } from '@databases/model/field';
import { type MutableValue } from '@databases/model/value';
import {
  type CollabFilter,
  type CollabFilterBooleanExpression,
  type CollabFilterContains,
  type CollabFilterEmpty,
  type CollabFilterEquals,
  type CollabFilterGreaterLess,
  type CollabFilterJSON,
  type CollabFilterJSONBooleanExpression,
  type CollabFilterJSONContains,
  type CollabFilterJSONEmpty,
  type CollabFilterJSONEquals,
  type CollabFilterJSONGreaterLess,
  CollabFilterType,
  type MutableCollabFilterBooleanExpression,
  type MutableCollabFilterContains,
  type MutableCollabFilterEmpty,
  type MutableCollabFilterEquals,
  type MutableCollabFilterGreaterLess
} from '@databases/model/views/view-filter';
import * as Y from 'yjs';

import { type DefaultValueConverters } from '../default-value-converters';
import { LazyMapElement } from '../helpers/lazy-map-element';
import { SynchronizedArray } from '../helpers/synchronized-array';
import { SynchronizedMapElement } from '../helpers/synchronized-map-element';
import { SynchronizedRecord } from '../helpers/synchronized-record';
import { type YValueType } from '../values/types';
import { YTextValue } from '../values/y-text-value';
import { ValueProperties, valueForType, valueFromJSON } from '../y-entry';
import { FieldMapping } from '../y-field';

const FilterProperties = {
  id: 'i',
  type: 't',
  value: 'v',
  fieldId: 'f',
  not: 'n',
  orEqual: 'o'
};

enum YFilterType {
  AND = 'a',
  OR = 'o',
  EQUALS = 'eq',
  CONTAINS = 'c',
  EMPTY = 'em',
  GREATER = 'g',
  LESS = 'l'
}

const FilterTypeMap: Record<CollabFilterType, YFilterType> = {
  [CollabFilterType.AND]: YFilterType.AND,
  [CollabFilterType.OR]: YFilterType.OR,
  [CollabFilterType.EQUALS]: YFilterType.EQUALS,
  [CollabFilterType.CONTAINS]: YFilterType.CONTAINS,
  [CollabFilterType.EMPTY]: YFilterType.EMPTY,
  [CollabFilterType.GREATER]: YFilterType.GREATER,
  [CollabFilterType.LESS]: YFilterType.LESS
};

export type YFilter =
  | YFilterBooleanExpression
  | YFilterEquals
  | YFilterContains
  | YFilterEmpty
  | YFilterGreaterLess;

class YFilterBooleanExpression
  extends SynchronizedRecord
  implements CollabFilterBooleanExpression, MutableCollabFilterBooleanExpression
{
  #value: SynchronizedMapElement<SynchronizedArray<YFilter>>;

  constructor(
    data: Y.Map<unknown>,
    private defaultValueConverters: DefaultValueConverters
  ) {
    super(data);

    this.#value = new SynchronizedMapElement<SynchronizedArray<YFilter>>(
      data,
      FilterProperties.value,
      (maybeArray) => {
        if (maybeArray instanceof Y.Array) {
          return new SynchronizedArray<YFilter>(maybeArray, (arrayVal) => {
            if (arrayVal instanceof Y.Map) {
              return filterMapper(arrayVal, defaultValueConverters);
            }
            return undefined;
          });
        }
        return undefined;
      },
      () => new Y.Array()
    );
  }

  get id(): string {
    return this.getString(FilterProperties.id, '');
  }

  set id(val: string) {
    this.set(FilterProperties.id, val);
  }

  get type(): CollabFilterType.AND | CollabFilterType.OR {
    switch (
      this.getStringEnum<Array<YFilterType.AND | YFilterType.OR>>(
        FilterProperties.type,
        [YFilterType.AND, YFilterType.OR],
        YFilterType.AND
      )
    ) {
      case YFilterType.AND:
        return CollabFilterType.AND;
      case YFilterType.OR:
        return CollabFilterType.OR;
    }
  }

  set type(val: CollabFilterType.OR | CollabFilterType.AND) {
    this.set(FilterProperties.type, FilterTypeMap[val]);
  }

  get value() {
    return this.#value?.value?.toArray() ?? [];
  }

  add(filter: CollabFilterJSON): CollabFilter {
    const yfilter = yFilterFromJSON(filter, this.defaultValueConverters);
    this.#value.valueRequired.push([yfilter]);
    return yfilter;
  }

  setFilter(id: string, index: number, val: CollabFilterJSON): void {
    if (this.#value.valueRequired.get(index).id === id) {
      this.#value.valueRequired.delete(index);
      this.#value.valueRequired.insert(index, [
        yFilterFromJSON(val, this.defaultValueConverters)
      ]);
    }
  }

  remove(id: string, index: number): void {
    if (this.#value.valueRequired.get(index).id === id) {
      this.#value.value?.delete(index);
    }
  }

  toJSON(): CollabFilterJSONBooleanExpression {
    return {
      id: this.id,
      type: this.type,
      value: this.value.map((val) => val.toJSON())
    };
  }
}

class YFilterEquals
  extends SynchronizedRecord
  implements CollabFilterEquals, MutableCollabFilterEquals
{
  #value: LazyMapElement<YValueType>;

  constructor(
    data: Y.Map<unknown>,
    private defaultValueConverters: DefaultValueConverters
  ) {
    super(data);

    this.#value = new LazyMapElement(
      data,
      FilterProperties.value,
      (input) => {
        if (input instanceof Y.Map) {
          return valueForType(
            input,
            input.get(ValueProperties.type),
            false,
            defaultValueConverters
          );
        }
      },
      () => new Y.Map([[ValueProperties.type, FieldMapping[FieldType.Text]]]),
      (input, attach) => {
        return new YTextValue<FieldType.Text>(input, attach);
      }
    );
  }

  get id(): string {
    return this.getString(FilterProperties.id, '');
  }

  set id(val: string) {
    this.set(FilterProperties.id, val);
  }

  get type(): CollabFilterType.EQUALS {
    return CollabFilterType.EQUALS;
  }

  get fieldId(): string {
    return this.getString(FilterProperties.fieldId, '');
  }

  set fieldId(val: string) {
    this.set(FilterProperties.fieldId, val);
  }

  get not(): boolean {
    return this.getBoolean(FilterProperties.not, false);
  }

  setValue<T extends FieldType>(type: T): MutableValue<T> {
    if (this.#value.value.type !== type) {
      const value = valueForType(
        new Y.Map([[ValueProperties.type, FieldMapping[type]]]),
        FieldMapping[type],
        false,
        this.defaultValueConverters
      )!;

      this.#value.set(value.unwrap());

      return value as unknown as MutableValue<T>; // FIXME: can we improve this type?
    } else {
      return this.#value.value as unknown as MutableValue<T>; // FIXME: can we improve this type?
    }
  }

  getValue() {
    return this.#value.value;
  }

  toJSON(): CollabFilterJSONEquals {
    return {
      id: this.id,
      type: this.type,
      fieldId: this.fieldId,
      value: this.getValue().toJSON(),
      not: this.not
    };
  }
}

class YFilterContains
  extends SynchronizedRecord
  implements CollabFilterContains, MutableCollabFilterContains
{
  constructor(data: Y.Map<unknown>) {
    super(data);
  }

  get id(): string {
    return this.getString(FilterProperties.id, '');
  }

  set id(val: string) {
    this.set(FilterProperties.id, val);
  }

  get type(): CollabFilterType.CONTAINS {
    return CollabFilterType.CONTAINS;
  }

  get fieldId(): string {
    return this.getString(FilterProperties.fieldId, '');
  }

  set fieldId(val: string) {
    this.set(FilterProperties.fieldId, val);
  }

  get not(): boolean {
    return this.getBoolean(FilterProperties.not, false);
  }

  set not(val: boolean) {
    this.set(FilterProperties.not, val);
  }

  get value(): string {
    return this.getString(FilterProperties.value, '');
  }

  set value(val: string) {
    this.set(FilterProperties.value, val);
  }

  toJSON(): CollabFilterJSONContains {
    return {
      id: this.id,
      type: this.type,
      fieldId: this.fieldId,
      value: this.value,
      not: this.not
    };
  }
}

class YFilterEmpty
  extends SynchronizedRecord
  implements CollabFilterEmpty, MutableCollabFilterEmpty
{
  constructor(data: Y.Map<unknown>) {
    super(data);
  }

  get id(): string {
    return this.getString(FilterProperties.id, '');
  }

  set id(val: string) {
    this.set(FilterProperties.id, val);
  }

  get type(): CollabFilterType.EMPTY {
    return CollabFilterType.EMPTY;
  }

  get fieldId(): string {
    return this.getString(FilterProperties.fieldId, '');
  }

  set fieldId(val: string) {
    this.set(FilterProperties.fieldId, val);
  }

  get not(): boolean {
    return this.getBoolean(FilterProperties.not, false);
  }

  set not(val: boolean) {
    this.set(FilterProperties.not, val);
  }

  toJSON(): CollabFilterJSONEmpty {
    return {
      id: this.id,
      type: this.type,
      fieldId: this.fieldId,
      not: this.not
    };
  }
}

class YFilterGreaterLess
  extends SynchronizedRecord
  implements CollabFilterGreaterLess, MutableCollabFilterGreaterLess
{
  #value: LazyMapElement<YValueType>;

  constructor(
    data: Y.Map<unknown>,
    private defaultValueConverters: DefaultValueConverters
  ) {
    super(data);

    this.#value = new LazyMapElement(
      data,
      FilterProperties.value,
      (input) => {
        if (input instanceof Y.Map) {
          return valueForType(
            input,
            input.get(ValueProperties.type),
            false,
            defaultValueConverters
          );
        }
      },
      () => new Y.Map([[ValueProperties.type, FieldMapping[FieldType.Text]]]),
      (input, attach) => {
        return new YTextValue<FieldType.Text>(input, attach);
      }
    );
  }

  get id(): string {
    return this.getString(FilterProperties.id, '');
  }

  set id(val: string) {
    this.set(FilterProperties.id, val);
  }

  get type(): CollabFilterType.GREATER | CollabFilterType.LESS {
    switch (
      this.getStringEnum<Array<YFilterType.GREATER | YFilterType.LESS>>(
        FilterProperties.type,
        [YFilterType.GREATER, YFilterType.LESS],
        YFilterType.GREATER
      )
    ) {
      case YFilterType.GREATER:
        return CollabFilterType.GREATER;
      case YFilterType.LESS:
        return CollabFilterType.LESS;
    }
  }

  get fieldId(): string {
    return this.getString(FilterProperties.fieldId, '');
  }

  set fieldId(val: string) {
    this.set(FilterProperties.fieldId, val);
  }

  get orEqual(): boolean {
    return this.getBoolean(FilterProperties.orEqual, false);
  }

  set orEqual(val: boolean) {
    this.set(FilterProperties.orEqual, val);
  }

  setValue<T extends FieldType>(type: T): MutableValue<T> {
    const value = valueForType(
      new Y.Map([[ValueProperties.type, FieldMapping[type]]]),
      FieldMapping[type],
      false,
      this.defaultValueConverters
    )!;

    this.#value.set(value.unwrap());

    return value as unknown as MutableValue<T>; // FIXME: can we improve this type?
  }

  getValue() {
    return this.#value.value;
  }

  toJSON(): CollabFilterJSONGreaterLess {
    return {
      id: this.id,
      type: this.type,
      fieldId: this.fieldId,
      orEqual: this.orEqual,
      value: this.getValue().toJSON()
    };
  }
}

export const filterMapper = (
  data: Y.Map<unknown>,
  defaultValueConverters: DefaultValueConverters
): YFilter | undefined => {
  switch (data.get(FilterProperties.type)) {
    case YFilterType.AND:
    case YFilterType.OR:
      return new YFilterBooleanExpression(data, defaultValueConverters);
    case YFilterType.EQUALS:
      return new YFilterEquals(data, defaultValueConverters);
    case YFilterType.CONTAINS:
      return new YFilterContains(data);
    case YFilterType.EMPTY:
      return new YFilterEmpty(data);
    case YFilterType.GREATER:
    case YFilterType.LESS:
      return new YFilterGreaterLess(data, defaultValueConverters);
  }
};

export const yFilterFromJSON = (
  filterJSON: CollabFilterJSON,
  defaultValueConverters: DefaultValueConverters
): YFilter => {
  switch (filterJSON.type) {
    case CollabFilterType.AND:
    case CollabFilterType.OR:
      return new YFilterBooleanExpression(
        new Y.Map([
          [FilterProperties.id, filterJSON.id],
          [FilterProperties.type, FilterTypeMap[filterJSON.type]],
          [
            FilterProperties.value,
            Y.Array.from(
              filterJSON.value.map(
                (child) => yFilterFromJSON(child, defaultValueConverters).data
              )
            )
          ]
        ]),
        defaultValueConverters
      );
    case CollabFilterType.EQUALS:
      return new YFilterEquals(
        new Y.Map([
          [FilterProperties.id, filterJSON.id],
          [FilterProperties.type, YFilterType.EQUALS],
          [FilterProperties.fieldId, filterJSON.fieldId],
          [FilterProperties.not, filterJSON.not],
          [
            FilterProperties.value,
            valueFromJSON(filterJSON.value, false, defaultValueConverters)
          ]
        ]),
        defaultValueConverters
      );
    case CollabFilterType.CONTAINS:
      return new YFilterContains(
        new Y.Map([
          [FilterProperties.id, filterJSON.id],
          [FilterProperties.type, YFilterType.CONTAINS],
          [FilterProperties.fieldId, filterJSON.fieldId],
          [FilterProperties.not, filterJSON.not],
          [FilterProperties.value, filterJSON.value]
        ])
      );
    case CollabFilterType.EMPTY:
      return new YFilterEmpty(
        new Y.Map([
          [FilterProperties.id, filterJSON.id],
          [FilterProperties.type, YFilterType.EMPTY],
          [FilterProperties.fieldId, filterJSON.fieldId],
          [FilterProperties.not, filterJSON.not]
        ])
      );
    case CollabFilterType.GREATER:
    case CollabFilterType.LESS:
      return new YFilterGreaterLess(
        new Y.Map([
          [FilterProperties.id, filterJSON.id],
          [FilterProperties.type, FilterTypeMap[filterJSON.type]],
          [FilterProperties.fieldId, filterJSON.fieldId],
          [FilterProperties.orEqual, filterJSON.orEqual],
          [
            FilterProperties.value,
            valueFromJSON(filterJSON.value, false, defaultValueConverters)
          ]
        ]),
        defaultValueConverters
      );
  }
};
